// frontend/src/components/games/GameCard.jsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFavorites } from "../../context/FavoritesContext";
import { useAuth } from "../../AuthContext";
import {
  trackGameInteraction,
  submitRecommendationFeedback,
} from "../../api/games";
import { recordInteraction, INTERACTION_TYPES } from "../../api/interactions";
import { getPlatformFamilies } from "../../utils/platforms";
import Tooltip from "../ui/Tooltip";
import ShareIcon from "../ui/ShareIcon";

const ICON_BTN =
  "inline-flex items-center justify-center h-8 w-8 rounded-full text-white transition-all duration-150 hover:scale-110";

const GameCard = ({
  game,
  onShare,
  showActions = true,
  loading = false,
  compact = false,
  recommendedFrom = null,
  onCrazierGame = null,
  source = "browsing", // Track where the card is being displayed
  handleRefresh = false,
}) => {
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { user, isBlacklisted, addToBlacklist, removeFromBlacklist, hideGame } =
    useAuth();
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const startTimeRef = useRef(null);
  const intersectionRef = useRef(null);

  const isGameFavorite = game ? isFavorite(game.id) : false;
  const isGameBlacklisted = game && user ? isBlacklisted(game.id) : false;

  // Track time spent on card (when visible)
  useEffect(() => {
    if (!game || !user) return;
    const node = intersectionRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startTimeRef.current = Date.now();
          } else if (startTimeRef.current) {
            const timeSpentMs = Date.now() - startTimeRef.current;
            const timeSpentSeconds = Math.floor(timeSpentMs / 1000);
            if (timeSpentSeconds >= 3) {
              recordInteraction(game.id, INTERACTION_TYPES.TIME_SPENT, {
                timeSpentSeconds,
                type: "card_view",
                source: source,
              });
            }
            startTimeRef.current = null;
          }
        });
      },
      { threshold: 0.5 },
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [game, user, source]);

  if (loading) {
    return (
      <div className="h-[520px] rounded-3xl bg-[#0f141e]/85 flex flex-col overflow-hidden animate-pulse">
        <div className="h-[210px] bg-white/10" />
        <div className="p-4 flex-1 flex flex-col gap-2">
          <div className="h-8 bg-white/10 rounded" />
          <div className="h-5 w-3/5 bg-white/10 rounded" />
          <div className="h-14 mt-2 bg-white/10 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!game) {
    return null;
  }

  const handleCardClick = async () => {
    await recordInteraction(game.id, INTERACTION_TYPES.VIEW_DETAILS, {
      type: "card_click",
      source: source,
      recommendedFrom: recommendedFrom,
    });
    trackGameInteraction(game.id, "view");
    navigate(`/game/${game.id}`);
  };

  const handleFavorite = async (e) => {
    e.stopPropagation();
    if (game) {
      await toggleFavorite(game.id);
      const isNowFavorite = isGameFavorite;
      await recordInteraction(game.id, INTERACTION_TYPES.FAVORITE_ADD, {
        type: "card_action",
        source: source,
        previousState: isNowFavorite ? "favorited" : "unfavorited",
      });
      trackGameInteraction(game.id, "favorite");
    }
  };

  const handleBlacklist = async (e) => {
    e.stopPropagation();
    if (!user || !game) return;

    if (isGameBlacklisted) {
      await removeFromBlacklist(game.id);
      handleRefresh();
    } else {
      await addToBlacklist(game.id);
      await recordInteraction(game.id, INTERACTION_TYPES.BLACKLIST_ADD, {
        type: "card_action",
        source: source,
      });
    }
    trackGameInteraction(game.id, "blacklist");
  };

  // eslint-disable-next-line no-unused-vars
  const handleHideGame = async (e) => {
    e.stopPropagation();
    if (!user || !game) return;

    await hideGame(game.id);
    await recordInteraction(game.id, INTERACTION_TYPES.HIDE_GAME, {
      type: "card_action",
      source: source,
    });

    if (onCrazierGame) {
      onCrazierGame(game.id);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();

    await recordInteraction(game.id, INTERACTION_TYPES.SHARE_GAME, {
      type: "card_action",
      source: source,
    });

    trackGameInteraction(game.id, "share");

    if (onShare) {
      onShare(game);
    } else {
      if (navigator.share) {
        navigator.share({
          title: game.title || game.name,
          text: game.summary || `Check out ${game.title || game.name}`,
          url: `${window.location.origin}/game/${game.id}`,
        });
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleRecommendationFeedback = async (feedbackType) => {
    if (!recommendedFrom || feedbackGiven) return;

    try {
      await recordInteraction(
        game.id,
        feedbackType === "helpful"
          ? INTERACTION_TYPES.RECOMMENDATION_HELPFUL
          : INTERACTION_TYPES.RECOMMENDATION_NOT_HELPFUL,
        {
          type: "recommendation_feedback",
          source: source,
          recommendedFrom: recommendedFrom,
        },
      );

      await submitRecommendationFeedback(
        game.id,
        recommendedFrom,
        feedbackType,
      );
      setFeedbackGiven(true);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
  };

  const formatReleaseDate = (dateString) => {
    if (!dateString) return "TBA";
    try {
      const date = new Date(dateString);
      return date.getFullYear();
    } catch {
      return "TBA";
    }
  };

  const getGenresArray = (genres) => {
    if (!genres) return [];
    if (Array.isArray(genres)) return genres;
    if (typeof genres === "string") {
      try {
        const parsed = JSON.parse(genres);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const getGameModes = (gameModes) => {
    if (!gameModes) return [];
    if (Array.isArray(gameModes)) return gameModes;
    if (typeof gameModes === "string") {
      try {
        const parsed = JSON.parse(gameModes);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const getCleanTags = (tags) => {
    if (!tags) return [];
    let tagArray = [];

    if (Array.isArray(tags)) {
      tagArray = tags;
    } else if (typeof tags === "string") {
      try {
        const parsed = JSON.parse(tags);
        tagArray = Array.isArray(parsed) ? parsed : [];
      } catch {
        tagArray = [];
      }
    }

    const irrelevantTags = [
      "steam achievements",
      "trading cards",
      "steam cloud",
      "achievements",
      "full controller support",
      "partial controller support",
      "steam workshop",
      "steam leaderboards",
      "includes source sdk",
      "steam trading cards",
      "commentary available",
      "captions available",
      "stats",
      "singleplayer",
      "single player",
      "single-player",
      "multiplayer",
      "multi-player",
      "online multiplayer",
      "in-app purchases",
      "includes level editor",
      "downloadable content",
      "dlc",
      "expansion",
      "season pass",
      "game",
      "video game",
      "pc game",
    ];

    return tagArray
      .filter((tag) => {
        const tagName =
          typeof tag === "string"
            ? tag.toLowerCase()
            : (tag.name || "").toLowerCase();
        return !irrelevantTags.some((irrelevant) =>
          tagName.includes(irrelevant),
        );
      })
      .slice(0, 12);
  };

  const userScore = game.user_score || game.rating || 0;
  const hasGameModes = () => {
    const gameModes = getGameModes(game.game_modes || game.gameModes);
    const modesDisplay = [];

    const hasSinglePlayer =
      gameModes.some((mode) =>
        typeof mode === "string"
          ? mode.toLowerCase().includes("single")
          : mode.name?.toLowerCase().includes("single"),
      ) || game.singleplayer;

    const hasMultiplayer =
      gameModes.some((mode) =>
        typeof mode === "string"
          ? mode.toLowerCase().includes("multi")
          : mode.name?.toLowerCase().includes("multi"),
      ) || game.multiplayer;

    if (hasSinglePlayer) modesDisplay.push("Single Player");
    if (hasMultiplayer) modesDisplay.push("Multiplayer");

    if (modesDisplay.length === 0) {
      const tags = getCleanTags(game.tags);
      const hasSpTag = tags.some((tag) =>
        typeof tag === "string"
          ? tag.toLowerCase().includes("single")
          : tag.name?.toLowerCase().includes("single"),
      );
      const hasMpTag = tags.some((tag) =>
        typeof tag === "string"
          ? tag.toLowerCase().includes("multi")
          : tag.name?.toLowerCase().includes("multi"),
      );
      if (hasSpTag) modesDisplay.push("Single Player");
      if (hasMpTag) modesDisplay.push("Multiplayer");
    }
    return modesDisplay;
  };

  const gameModesList = hasGameModes();
  const platformFamilies = getPlatformFamilies(game.platforms).families;
  const genresList = getGenresArray(game.genres);
  const cleanTags = getCleanTags(game.tags || game.genres);

  const relevanceScore = game.relevanceScore || null;

  // Bounded 0-100 match percentage for display. Prefer the normalized
  // matchPercent from the API; fall back to a clamped relevanceScore so the
  // badge can never show values outside the 0-100% range.
  const matchPercent =
    game.matchPercent != null
      ? Math.max(0, Math.min(100, Math.round(game.matchPercent)))
      : relevanceScore != null
      ? Math.max(0, Math.min(100, Math.round(relevanceScore * 100)))
      : null;

  const scoreColor =
    userScore >= 4 ? "#7CB342" : userScore >= 3 ? "#F4B400" : "#FF6B6B";

  // Tags open a (future) tag landing page listing all games for that tag.
  // Routes to a real, non-breaking path today; point it at the dedicated
  // page later without touching the card.
  const handleTagClick = (e, tagName) => {
    e.stopPropagation();
    if (!tagName) return;
    navigate(`/tags/${encodeURIComponent(tagName)}`);
  };

  const tagLabel = (tag) =>
    typeof tag === "string" ? tag : tag?.name || String(tag ?? "");

  return (
    <div
      ref={intersectionRef}
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleCardClick();
        }
      }}
      className="game-card group relative h-full min-h-[520px] w-full max-w-[340px] mx-auto flex flex-col rounded-3xl bg-gradient-to-br from-[#141024]/95 to-[#0b0916]/95 backdrop-blur-md border border-white/10 shadow-card transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-card-hover hover:border-brand-gold/60 cursor-pointer"
    >
      {/* Image header */}
      <div className="relative w-full h-[200px] overflow-hidden rounded-t-3xl bg-gradient-to-br from-[#0A0E17] to-[#1A1F2E]">
        <img
          src={
            imageError
              ? "/assets/images/placeholder.jpg"
              : game.cover_image ||
                game.cover_url ||
                game.image ||
                "/assets/images/placeholder.jpg"
          }
          alt={game.title || game.name}
          title={`View details for ${game.title || game.name}`}
          className="w-full h-full object-cover object-center block transition-transform duration-500 group-hover:scale-105"
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setImageLoading(false);
            setImageError(true);
          }}
          draggable={false}
        />
        {/* legibility gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0916] via-[#0b0916]/30 to-transparent pointer-events-none" />

        {imageLoading && <div className="absolute inset-0 gg-shimmer" />}

        {/* Title over image */}
        <div className="absolute inset-x-0 bottom-0 p-3.5 z-[2]">
          <h3 className="font-heading font-extrabold text-[1.05rem] leading-tight text-white line-clamp-2 drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)]">
            {game.title || game.name}
          </h3>
        </div>
      </div>

      {/* Badges — rendered on the card layer so their tooltips aren't clipped */}
      {matchPercent != null && (
        <Tooltip
          wide
          side="bottom"
          className="absolute top-3 left-3 z-20"
          label={`${matchPercent}% match, how well this game fits your search and taste`}
        >
          <span className="rounded-full px-2.5 py-1 text-[0.68rem] font-bold text-[#0b0916] bg-gold-gradient shadow-[0_2px_10px_rgba(239,191,4,0.4)] cursor-help">
            {matchPercent}% Match
          </span>
        </Tooltip>
      )}

      {game.is_new && (
        <Tooltip
          side="bottom"
          className={`absolute left-3 z-20 ${matchPercent != null ? "top-11" : "top-3"}`}
          label="Recently added to the catalogue"
        >
          <span className="rounded-full px-2.5 py-1 text-[0.62rem] font-bold text-white bg-gradient-to-br from-[#7CB342] to-[#A7C957] shadow-[0_2px_8px_rgba(0,0,0,0.3)] cursor-help">
            NEW
          </span>
        </Tooltip>
      )}

      <Tooltip
        side="left"
        className="absolute top-3 right-3 z-20"
        label={userScore > 0 ? `Player rating: ${userScore.toFixed(1)} out of 5` : "Not yet rated"}
      >
        <span
          className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.8rem] font-bold text-white bg-[#0b0916]/85 backdrop-blur-sm shadow-[0_4px_12px_rgba(0,0,0,0.35)] cursor-help"
          style={{ border: `1.5px solid ${scoreColor}` }}
        >
          <span style={{ color: scoreColor }}>★</span>
          <span>{userScore > 0 ? userScore.toFixed(1) : "N/A"}</span>
        </span>
      </Tooltip>

      {/* Body */}
      <div className="flex-1 flex flex-col gap-2.5 p-4 sm:p-5 pt-3.5">
        {/* Meta: release year + game modes */}
        <div className="flex items-center gap-2 flex-wrap text-white/80">
          <Tooltip label="Release year">
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-white cursor-help">
              <span className="text-brand-gold">📅</span>
              {formatReleaseDate(game.release_date)}
            </span>
          </Tooltip>
          {gameModesList.map((mode, idx) => (
            <span key={idx} className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <Tooltip label={`Game mode: ${mode}`}>
                <span
                  className="text-xs font-medium cursor-help"
                  style={{ color: mode === "Single Player" ? "#A7C957" : "#F4B400" }}
                >
                  {mode}
                </span>
              </Tooltip>
            </span>
          ))}
        </div>

        {/* Platforms (families) + genres — consistent with the detail page */}
        {(platformFamilies.length > 0 || (genresList.length > 0 && !compact)) && (
          <div className="flex flex-wrap gap-1.5">
            {platformFamilies.map((f) => (
              <Tooltip
                key={f.family}
                wide={f.members.length > 1}
                label={
                  f.members.length > 1
                    ? `Available on ${f.family}: ${f.members.join(", ")}`
                    : `Available on ${f.members[0] || f.family}`
                }
              >
                <span className="text-[0.6rem] font-semibold h-[22px] flex items-center px-2 rounded-full text-brand-gold border border-brand-gold/50 bg-brand-gold/10 cursor-help">
                  {f.family}
                </span>
              </Tooltip>
            ))}
            {!compact &&
              genresList.slice(0, 2).map((genre, index) => {
                const genreLabel =
                  genre?.genre?.name ||
                  genre?.name ||
                  (typeof genre === "string" ? genre : "Unknown");
                return (
                  <Tooltip key={`g-${index}`} label={`Genre: ${genreLabel}`}>
                    <span className="text-[0.6rem] font-semibold h-[22px] flex items-center px-2 rounded-full text-neon-lime border border-neon-lime/50 bg-neon-lime/10 cursor-help">
                      {genreLabel}
                    </span>
                  </Tooltip>
                );
              })}
          </div>
        )}

        {/* Short description */}
        {game.short_description && !compact && (
          <p className="text-[0.72rem] leading-snug text-white/65 line-clamp-2">
            {game.short_description}
          </p>
        )}

        {/* Tags / features — visible + clickable (open a tag page in future) */}
        {cleanTags.length > 0 && !compact && (
          <div className="mt-auto pt-1">
            <p className="text-[0.6rem] font-bold uppercase tracking-wider text-white/40 mb-1.5">
              Tags &amp; features
            </p>
            <div className="flex flex-wrap gap-1.5">
              {cleanTags.slice(0, 6).map((tag, index) => {
                const label = tagLabel(tag);
                return (
                  <Tooltip key={index} label={`Browse ${label} games`}>
                    <button
                      type="button"
                      onClick={(e) => handleTagClick(e, label)}
                      className="rounded-full px-2.5 py-1 text-[0.62rem] font-semibold text-white/85 bg-brand-purple/15 border border-brand-purple/30 transition-all duration-150 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-gold/10 hover:-translate-y-0.5"
                    >
                      #{label}
                    </button>
                  </Tooltip>
                );
              })}
              {cleanTags.length > 6 && (
                <Tooltip label="See all tags on the details page">
                  <span
                    onClick={handleCardClick}
                    className="rounded-full px-2.5 py-1 text-[0.62rem] font-semibold text-white/50 border border-white/10 bg-white/[0.03] cursor-pointer hover:text-white/80"
                  >
                    +{cleanTags.length - 6} more
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        {/* Developer — links to the developer's games */}
        {game.developer && !compact && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/company/developer/${encodeURIComponent(game.developer)}`);
            }}
            title={`See all games by ${game.developer}`}
            className="self-start text-[0.65rem] italic text-white/45 transition-colors hover:text-brand-gold hover:underline"
          >
            {game.developer}
          </button>
        )}
      </div>

      {showActions && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0 mt-auto">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-1">
              <Tooltip label={isGameFavorite ? "Remove from favorites" : "Add to favorites"}>
                <button
                  type="button"
                  onClick={handleFavorite}
                  aria-label={isGameFavorite ? "Remove from favorites" : "Add to favorites"}
                  className={`${ICON_BTN} hover:bg-brand-gold/15`}
                >
                  <span style={{ color: isGameFavorite ? "#F4B400" : "#fff" }}>
                    {isGameFavorite ? "♥" : "♡"}
                  </span>
                </button>
              </Tooltip>

              <Tooltip label="Share this game">
                <button
                  type="button"
                  onClick={handleShare}
                  aria-label="Share this game"
                  className={`${ICON_BTN} hover:text-[#A7C957] hover:bg-[#A7C957]/15`}
                >
                  <ShareIcon size={17} />
                </button>
              </Tooltip>

              <Tooltip
                wide
                label={
                  user
                    ? isGameBlacklisted
                      ? "Remove from blacklist, show this game again"
                      : "Hide this game from your results & recommendations"
                    : "Sign in to hide this game"
                }
              >
                <button
                  type="button"
                  onClick={handleBlacklist}
                  disabled={!user}
                  aria-label="Hide this game"
                  className={[
                    ICON_BTN,
                    !user
                      ? "opacity-40 cursor-not-allowed"
                      : isGameBlacklisted
                        ? "text-red-500 hover:bg-red-500/15"
                        : "hover:text-red-500 hover:bg-red-500/15",
                  ].join(" ")}
                >
                  ⛔
                </button>
              </Tooltip>
            </div>

            <Tooltip label="View full details, screenshots & similar games">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/game/${game.id}`);
                }}
                className="rounded-full px-4 py-1.5 text-[0.7rem] font-bold text-[#0A0E17] bg-gradient-to-br from-[#F4B400] to-[#A7C957] shadow-[0_4px_12px_rgba(244,180,0,0.3)] transition-all duration-200 hover:from-[#A7C957] hover:to-[#7CB342] hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(244,180,0,0.4)]"
              >
                Details
              </button>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameCard;
